/**
 * The full-screen photo viewer's rotate / zoom state — view-only: nothing here
 * changes the stored photo, the AI reading or the condition report.
 */

export type Rotation = 0 | 90 | 180 | 270;

export const MIN_SCALE = 1;
export const MAX_SCALE = 6;
/** One zoom button press. */
export const ZOOM_STEP = 1.5;
/** Double-click / double-tap target when at 100%. */
export const DOUBLE_TAP_SCALE = 2.5;

export interface ViewerTransform {
  rotation: Rotation;
  scale: number;
  /** Pan offset in screen pixels; only meaningful while zoomed in. */
  x: number;
  y: number;
}

export const IDENTITY: ViewerTransform = { rotation: 0, scale: 1, x: 0, y: 0 };

export function rotate(t: ViewerTransform, direction: 1 | -1): ViewerTransform {
  const rotation = ((((t.rotation + direction * 90) % 360) + 360) % 360) as Rotation;
  return { ...t, rotation };
}

export function clampScale(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

/** Zoom by a factor. Back at 100% the pan resets, so the photo re-centres. */
export function zoomBy(t: ViewerTransform, factor: number): ViewerTransform {
  const scale = clampScale(t.scale * factor);
  if (scale === MIN_SCALE) return { ...t, scale, x: 0, y: 0 };
  // Keep the same point under the centre while zooming.
  const ratio = scale / t.scale;
  return { ...t, scale, x: t.x * ratio, y: t.y * ratio };
}

export function zoomTo(t: ViewerTransform, scale: number): ViewerTransform {
  return zoomBy(t, scale / t.scale);
}

/** Panning only makes sense when there is more photo than screen. */
export function pan(t: ViewerTransform, dx: number, dy: number): ViewerTransform {
  if (t.scale <= MIN_SCALE) return t;
  return { ...t, x: t.x + dx, y: t.y + dy };
}

/** A quarter turn swaps which screen edge bounds the photo. */
export function isSideways(t: ViewerTransform): boolean {
  return t.rotation === 90 || t.rotation === 270;
}

export function toCss(t: ViewerTransform): string {
  return `translate(${t.x}px, ${t.y}px) scale(${t.scale}) rotate(${t.rotation}deg)`;
}

export function zoomLabel(t: ViewerTransform): string {
  return `${Math.round(t.scale * 100)}%`;
}
